import { useEffect, useState } from "react";
import { Megaphone, X } from "lucide-react";
import api from "@/lib/api";

const SESSION_KEY = "nb_announcement_seen_this_session";

/**
 * Feb-19 v2: Shows admin announcements ONCE per login session.
 * - On every fresh login (new tab/session), sessionStorage is empty → popup shows.
 * - After user closes → sessionStorage flag set → popup does NOT reshow on other pages.
 * - Logout clears sessionStorage (see AuthContext.logout) → next login shows it again.
 * The backend `last_seen_announcement_at` is still used to avoid showing already-old
 * announcements — but the per-session gate is the primary one for the "one-per-login" UX.
 */
export default function AnnouncementPopup() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Session-level gate: only show once per login session
    if (typeof window !== "undefined" && window.sessionStorage?.getItem(SESSION_KEY)) return;
    (async () => {
      try {
        const { data } = await api.get("/announcements/unseen");
        if (data?.announcements?.length) {
          setItems(data.announcements);
          setOpen(true);
        } else {
          // No new announcements — still mark as "checked this session" so we don't refetch
          window.sessionStorage?.setItem(SESSION_KEY, "1");
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const close = async () => {
    setOpen(false);
    try { window.sessionStorage?.setItem(SESSION_KEY, "1"); } catch (e) { /* ignore */ }
    try { await api.post("/announcements/ack"); } catch (e) { /* ignore */ }
  };

  if (!open || items.length === 0) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="announcement-popup">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close}/>
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-[#8AE234]/40 bg-[#0F1626] shadow-2xl shadow-[#8AE234]/10 max-h-[85dvh] overflow-y-auto">
        <div className="flex items-start justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-[#8AE234]/10 border border-[#8AE234]/30">
              <Megaphone size={16} className="text-[#8AE234]"/>
            </div>
            <div>
              <div className="font-heading text-white font-semibold text-sm">Company Announcement{items.length > 1 ? "s" : ""}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">{items.length} new</div>
            </div>
          </div>
          <button data-testid="announcement-close" onClick={close} className="text-slate-400 hover:text-white"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-4">
          {items.map((a) => (
            <div key={a.id} className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid={`announcement-${a.id}`}>
              {a.title && <div className="font-heading text-white font-semibold text-base mb-1">{a.title}</div>}
              <div className="text-slate-300 text-sm whitespace-pre-wrap">{a.body || a.message || ""}</div>
              <div className="text-[10px] text-slate-500 mt-2">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
          <button data-testid="announcement-ack" onClick={close} className="nb-btn-primary w-full text-sm">Got it</button>
        </div>
      </div>
    </div>
  );
}
